export const projects = [
    {
        id: 1,
        title: "Useless",
        category: "AI Art",
        year: "2024",
        description: "An ongoing AI project imagining surreal objects through vivid prompts. Each piece explores light, texture, material, and emotion.",
        image: "/images/useless.webp",
        link: "/work/useless",
        externalLink: "https://www.jacobfaulkner.co.uk/work/useless/", // open static project folder
        tags: ["AI", "Generative Art", "3D"],
        aspect: "4:5",
        videoLink: "https://example.com/useless-video",
        customContent: "An ongoing exploration of AI-generated surreal objects that challenge our perception of utility and beauty. Each piece is crafted through carefully engineered prompts that explore the intersection of light, texture, material, and emotion. The project examines what makes an object 'useless' in our consumer-driven society while celebrating the aesthetic potential of the absurd and impractical."
    },
    {
        id: 2,
        title: "Earth Day",
        category: "Interactive Installation",
        year: "2025",
        description: "Interactive webapp presenting 3D scanned models of waste in Bristol's castle park.",
        image: "/images/earth-day/earth-day.jpg",
        link: "/work/earth-day",
        externalLink: "/work/earth-day/", // open static project folder
        tags: ["Interactive", "Lidar Scanning", "Environmental","Documentary"],
        aspect: "5:4",
        videoLink: "https://example.com/earth-day-video",
        customContent: "An environmental awareness documentary that uses advanced 3D scanning technology to capture and document waste materials found in Bristol's Castle Park. The project combines LiDAR scanning with interactive storytelling to create an immersive experience that highlights the environmental impact of consumer waste. Visitors can explore detailed 3D models of discarded objects, learning about their decomposition timelines and environmental consequences through an interactive digital installation."
    },
    {
        id: 3,
        title: "Turbo VR",
        category: "Virtual Reality",
        year: "2023 (ongoing)",
        description: "A self-produced PlayStation 1-style VR arcade game about Turbo Island, exploring labour, waste, and public space in Stokes Croft.",
        image: "/images/turbo-vr/poster1.webp",
        link: "/work/turbo-vr",
        tags: ["VR", "Cultural", "Game Development"],
        aspect: "5:4",
        customContent: `<p>Since 2023, I've been developing Turbo VR, a PlayStation 1-style VR arcade game about Turbo Island — the legendary spot in Stokes Croft.</p>

<img src="/images/turbo-vr/poster1.webp" alt="Turbo VR poster artwork" style="width: 100%; max-width: 900px; margin: 1.75rem 0; border-radius: 8px;" />

<h2>How It Works</h2>

<p>Players have 60 seconds to burn as much dropped rubbish as possible. Some items earn points, while others reduce your score. At the end of the timer, your name and score get added to an online leaderboard for everyone to see.</p>

<p><strong>🔥 The game timer starts when you burn a pallet on the floor using a clipper</strong> — setting off the countdown in true Turbo Island fashion.</p>

<video src="/images/turbo-vr/Gameplay2.webm" autoplay loop muted playsInline style="width: 100%; max-width: 900px; margin: 1.75rem 0; border-radius: 8px; display: block;"></video>

<h2>Over 40 Burnable Items (and Counting)</h2>

<p>Some of the objects currently in the game include:</p>

<img src="/images/turbo-vr/items3.jpg" alt="Turbo VR burnable items selection" style="width: 100%; max-width: 900px; margin: 1.75rem 0; border-radius: 8px;" />

<ul>
  <li>Biblos wrap (of course)</li>
  <li>Crack Magazine</li>
  <li>Voi scooters (yes, the game was in development during the Voi days)</li>
  <li>Wallace &amp; Gromit VHS</li>
  <li>Camo bucket hats</li>
  <li>Noz canisters</li>
  <li>Grinders &amp; vape pens</li>
  <li>And more — with new items being added over time.</li>
</ul>

<h2>Customisable &amp; Community-Driven</h2>

<p>The game includes custom skins for both player hands and the wall. I'm inviting local artists and creatives to design wall skins — if you'd like to contribute, I can send you the 3D model and instructions. Approved designs will be added in future releases.</p>

<video src="/images/turbo-vr/Hands-wall4.webm" autoplay loop muted playsInline style="width: 100%; max-width: 900px; margin: 1.75rem 0; border-radius: 8px; display: block;"></video>

<h3>Unlockable Wall Skins</h3>

<p>Players can unlock new wall skins by hitting certain scores or completing specific in-game actions.</p>

<video src="/images/turbo-vr/Area5.webm" autoplay loop muted playsInline style="width: 100%; max-width: 900px; margin: 1.75rem 0; border-radius: 8px; display: block;"></video>

<h2>Sound Design</h2>

<p>The jungle and foley soundtrack was commissioned by Bigchoc, who also worked with me on sound design and foley — capturing real sounds from Stokes Croft. I also had help from James Bragg, who recorded a 360° field capture of Turbo Island (pre-fire), including sirens, chatter, and the general chaos of the area.</p>

<h2>Why Turbo VR?</h2>

<p>Turbo Island has always been more than just a patch of land — it's a cultural space. This game aims to be both fun and socially engaging, raising questions about:</p>

<ul>
  <li>How we use Turbo Island</li>
  <li>What it represents for Bristol's people</li>
  <li>Why it became an iconic space</li>
</ul>

<p>Now that Turbo Island has been auctioned off (2024), I think it's important to preserve its history — even digitally.</p>

<h2>Future Mechanics in Development</h2>

<p>Big thanks to Adam Stephenson for helping with some of the coding. I'm constantly working on new features and improvements, and I welcome ideas from the community about what would be fun to see in action. Planned features include:</p>

<ul>
  <li><strong>Open Record Player</strong> — Artists can submit short tracks that appear in-game as burnable records for players to unlock.</li>
  <li><strong>Spray Can Feature</strong> — Players will be able to spray custom graffiti onto the wall in real-time.</li>
  <li><strong>Co-op Mode</strong> — A two-player mode that lets you compete against friends in real time.</li>
</ul>

<h2>Playtesting &amp; Next Steps</h2>

<p>Since this is a self-produced project, there's no confirmed release date yet. I've already tested the game at Basement Beer and Advance Retreat and will be showcasing it again around Stokes Croft.</p>

<p>These sessions have been invaluable for fixing bugs, improving player experience, and gathering feedback on what people want to see as the game continues to develop.</p>

<div class="video-embed" style="margin-top: 2rem;">
  <iframe src="https://player.vimeo.com/video/1034252837?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
          width="100%"
          height="400"
          frameborder="0"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
          referrerpolicy="strict-origin-when-cross-origin"
          title="Turbo VR WIP"></iframe>
</div>`
    },
    {
        id: 4,
        title: "Liquidation — Glastonbury Festival, Shangri-La",
        category: "Festival Installation",
        year: "2024",
        description: "Ordos Studio (Jacob Faulkner) × Knobhead Industries\n\nWindow Installation · Public Art · Material Commentary",
        image: "/images/liquidation/liquidation (5).jpg",
        link: "/work/liquidation",
        tags: ["Installation", "Festival", "Collaboration","3D Modeling"],
        aspect: "16:9",
        images: [
            "/images/liquidation/liquidation (1).jpg",
            "/images/liquidation/liquidation (2).jpg",
            "/images/liquidation/liquidation (3).jpg",
            "/images/liquidation/liquidation (4).jpg",
            "/images/liquidation/liquidation (5).jpg",
            "/images/liquidation/liquidation (6).jpg",
            "/images/liquidation/liquidation (7).jpg",
            "/images/liquidation/liquidation (8).jpg",
            "/images/liquidation/liquidation (9).jpg",
            "/images/liquidation/liquidation (10).jpg",
            "/images/liquidation/liquidation (11).jpg"
        ],
        contentSections: [
            {
                title: "Overview",
                content: `<p>Liquidation was a satirical, street-facing window installation created for Shangri-La at Glastonbury Festival — a zone known for its anti-capitalist storytelling and politically charged visual culture. At its centre was a 30 cm ice sculpture of the pound sterling symbol (£), repeatedly melting and being replaced throughout the festival.</p>

<p>The piece transformed the £ symbol into a fragile, temporary object — a physical metaphor for consumerism, economic collapse, and the relentless erosion of personal assets under late capitalism.</p>`,
                imageIndices: []
            },
            {
                title: "The Brief",
                content: `<p>To create a window installation that literally visualised money melting away, using a recognisable economic symbol placed within an environment themed around everyday financial pressure.</p>`,
                imageIndices: []
            },
            {
                title: "Concept",
                content: `<p>Developed collaboratively with <a href="https://www.instagram.com/knobhead_industries" target="_blank">Knobhead Industries</a>, the installation turned the melting of a frozen £ symbol into a ritualistic performance. As each sculpture dissolved, the work highlighted:</p>

<ul>
<li>the instability of modern financial systems,</li>
<li>the absurdity of the consumer economy,</li>
<li>and the dark humour that defines Shangri-La's visual language.</li>
</ul>

<p>The interior walls were covered with custom wallpaper made from printed receipt paper showing monthly outgoing bills — turning personal economic strain into an immersive set design.</p>`,
                imageIndices: [4, 10] // liquidation (5).jpg and liquidation (11).jpg
            },
            {
                title: "My Role — <a href='http://www.jacobfaulkner.co.uk' target='_blank'>Ordos Studio</a> / Jacob Faulkner",
                content: `<ul>
<li>Concept development support, including early visual direction.</li>
<li>3D modelling the oversized £ symbol in Cinema 4D.</li>
<li>3D printing the 30 cm PLA master for mould creation.</li>
<li>Fabrication assistance, ensuring the geometry cast cleanly in ice.</li>
<li>Lighting support, especially for the backlit display.</li>
<li>Design and production of the receipt-paper wallpaper used inside the installation.</li>
</ul>

<p><a href="https://www.instagram.com/knobhead_industries" target="_blank">Knobhead Industries</a> led the core concept, fabrication build, mould-making workflow, installation, and the rotation of the melting sculptures throughout the festival.</p>`,
                imageIndices: [5, 6] // liquidation (6).jpg and liquidation (7).jpg
            },
            {
                title: "Process & Fabrication",
                content: `<h3>1. Digital Sculpt & Master Print</h3>

<ul>
<li>Modeled the pound sterling symbol digitally.</li>
<li>Produced a large-format PLA 3D print to serve as the mould master.</li>
</ul>

<h3>2. Silicone Mould Production</h3>

<ul>
<li>Multiple one-part silicone moulds were created for efficient recasting.</li>
<li>Each mould was filled and frozen externally.</li>
</ul>

<h3>3. Cyclical Melt Performance</h3>

<ul>
<li>Frozen £ symbols lasted 2–3 hours before fully collapsing.</li>
<li>Fresh symbols were rotated in, creating a continuous loop of "asset decay."</li>
</ul>`,
                imageIndices: [3, 1] // liquidation (4).jpg and liquidation (2).jpg
            },
            {
                title: "Installation",
                content: `<ul>
<li>Installed inside a pitched, street-facing window at Shangri-La.</li>
<li>Ice sculpture positioned on a plinth and backlit for maximum visual clarity.</li>
<li>Fans and reflective insulation were used to slow melting.</li>
<li>A custom drainage tray captured meltwater below the piece.</li>
<li>The interior was wrapped in receipt-paper wallpaper, reinforcing the theme of financial exhaustion.</li>
</ul>`,
                imageIndices: [2, 1, 8] // liquidation (3).jpg, liquidation (2).jpg, and liquidation (9).jpg
            },
            {
                title: "Outcome",
                content: `<p>Liquidation operated as a live, self-erasing artwork — a visually striking and darkly comedic meditation on the instability of value. Festival-goers repeatedly returned to witness new £ symbols melt down, making the concept feel alive and continuously unfolding.</p>

<p>The piece successfully combined material performance, social critique, and immersive design, fitting seamlessly within the rebellious ethos of Shangri-La.</p>`,
                imageIndices: [7, 0] // liquidation (8).jpg and liquidation (1).jpg
            }
        ],
        customContent: `<h2>Overview</h2>

<p>Liquidation was a satirical, street-facing window installation created for Shangri-La at Glastonbury Festival — a zone known for its anti-capitalist storytelling and politically charged visual culture. At its centre was a 30 cm ice sculpture of the pound sterling symbol (£), repeatedly melting and being replaced throughout the festival.</p>

<p>The piece transformed the £ symbol into a fragile, temporary object — a physical metaphor for consumerism, economic collapse, and the relentless erosion of personal assets under late capitalism.</p>

<h2>The Brief</h2>

<p>To create a window installation that literally visualised money melting away, using a recognisable economic symbol placed within an environment themed around everyday financial pressure.</p>

<h2>Concept</h2>

<p>Developed collaboratively with <a href="https://www.instagram.com/knobhead_industries" target="_blank">Knobhead Industries</a>, the installation turned the melting of a frozen £ symbol into a ritualistic performance. As each sculpture dissolved, the work highlighted:</p>

<ul>
<li>the instability of modern financial systems,</li>
<li>the absurdity of the consumer economy,</li>
<li>and the dark humour that defines Shangri-La's visual language.</li>
</ul>

<p>The interior walls were covered with custom wallpaper made from printed receipt paper showing monthly outgoing bills — turning personal economic strain into an immersive set design.</p>

<h2>My Role — <a href='http://www.jacobfaulkner.co.uk' target='_blank'>Ordos Studio</a> / Jacob Faulkner</h2>

<ul>
<li>Concept development support, including early visual direction.</li>
<li>3D modelling the oversized £ symbol in Cinema 4D.</li>
<li>3D printing the 30 cm PLA master for mould creation.</li>
<li>Fabrication assistance, ensuring the geometry cast cleanly in ice.</li>
<li>Lighting support, especially for the backlit display.</li>
<li>Design and production of the receipt-paper wallpaper used inside the installation.</li>
</ul>

<p><a href="https://www.instagram.com/knobhead_industries" target="_blank">Knobhead Industries</a> led the core concept, fabrication build, mould-making workflow, installation, and the rotation of the melting sculptures throughout the festival.</p>

<h2>Process & Fabrication</h2>

<h3>1. Digital Sculpt & Master Print</h3>

<ul>
<li>Modeled the pound sterling symbol digitally.</li>
<li>Produced a large-format PLA 3D print to serve as the mould master.</li>
</ul>

<h3>2. Silicone Mould Production</h3>

<ul>
<li>Multiple one-part silicone moulds were created for efficient recasting.</li>
<li>Each mould was filled and frozen externally.</li>
</ul>

<h3>3. Cyclical Melt Performance</h3>

<ul>
<li>Frozen £ symbols lasted 2–3 hours before fully collapsing.</li>
<li>Fresh symbols were rotated in, creating a continuous loop of "asset decay."</li>
</ul>

<h2>Installation</h2>

<ul>
<li>Installed inside a pitched, street-facing window at Shangri-La.</li>
<li>Ice sculpture positioned on a plinth and backlit for maximum visual clarity.</li>
<li>Fans and reflective insulation were used to slow melting.</li>
<li>A custom drainage tray captured meltwater below the piece.</li>
<li>The interior was wrapped in receipt-paper wallpaper, reinforcing the theme of financial exhaustion.</li>
</ul>

<h2>Outcome</h2>

<p>Liquidation operated as a live, self-erasing artwork — a visually striking and darkly comedic meditation on the instability of value. Festival-goers repeatedly returned to witness new £ symbols melt down, making the concept feel alive and continuously unfolding.</p>

<p>The piece successfully combined material performance, social critique, and immersive design, fitting seamlessly within the rebellious ethos of Shangri-La.</p>`
    },
    {
        id: 5,
        title: "Depth DJ - Cosmic Junkyard",
        category: "Interactive VJ Installation",
        year: "2023",
        description: "Interactive VJ setup using depth cameras and projection mapping.",
        image: "/images/depth-dj/cosmic-junkyard.jpg",
        link: "/work/depth-dj",
        tags: ["Installation", "VJ", "Projection Mapping"],
        aspect: "5:4",
        customContent: `<p>Commissioned by the Cosmic Junkyard venue at Boomtown festival, I created an interactive performance using TouchDesigner and Resolume. DJs and performers were 3D scanned via an Xbox Kinect, with their visuals dynamically reacting to the music in real time.</p>

<h2>Three Dimensional Capture</h2>

<p>The setup requires a camera to record the space, a laptop to translate that into a 3D scene and an output source such as a LED wall or projector. The output will be able to be mapped to surfaces and masked out.</p>

<p>The camera is a live feed of the performer that encapsulates them into a virtual scene that can be distorted and manipulated depending on the situation. This output can be altered with audio inputs making it more immersive for the audience.</p>

<p>Custom content can be made prior to the live event and can be layered up with the depth video.</p>`,
        images: [
            "/images/depth-dj/cosmic-junkyard.jpg",
            "/images/depth-dj/depths.jpg",
            "/images/depth-dj/depths02.jpg",
            "/images/depth-dj/cjy1.jpg"
        ]
    },
    {
        id: 7,
        title: "Zubieta World",
        category: "Virtual Environment",
        year: "2022",
        description: "Digital world creation collaboration with Zubieta exploring the intersection of 2D illustration and VR.",
        image: "/images/zubieta-world/ZubAR2.webm",
        link: "/work/zubieta-world",
        tags: ["VR", "2D Illustration", "Collaboration"],
        aspect: "5:4",
        images: [
            "/images/zubieta-world/zubietaar.webm",
            "/images/zubieta-world/zubietavr.webm",
            "/images/zubieta-world/ZubAR2.webm"
        ],
        customContent: `<p>A collaborative digital world creation project with artist Zubieta that bridges the gap between traditional 2D illustration and immersive VR experiences. The project explores how flat, illustrative artwork can be transformed into navigable 3D environments, creating unique spatial experiences that maintain the aesthetic qualities of hand-drawn art while adding depth and interactivity. This collaboration pushes the boundaries of how we experience illustrated narratives in digital spaces.</p>

<p>Commissioned through the Arts Council England by illustrator Zubieta, I transformed one of the artist's illustrations into a fully immersive virtual reality experience. This project required comprehensive conceptual planning, along with 3D modeling, texturing, and controls setup to bring the artwork to life in VR.</p>

<p>Within this commission, I wanted to ensure that the 3D environment worked on all devices and browsers without the need for any external or new technology, allowing all users to experience the project. If users had no VR device, the website would allow them to go into an AR mode, making the experience accessible regardless of their available hardware.</p>

<p>Also included was an image tracking project alongside this, which allowed users to track and see an animation of one of Zubieta's illustrations, further extending the interactive possibilities of the work.</p>

<p>The project can be found here: <a href="https://martazubieta.com/vr/" target="_blank" rel="noopener noreferrer">https://martazubieta.com/vr/</a></p>`
    },
    {
        id: 8,
        title: "Telegram Production Assistant",
        category: "Creative Technology",
        year: "2025",
        description: "A remote control system for my workstation, built as a Telegram bot that executes local commands and returns structured feedback.",
        image: "/images/telegram-bot/telegrambot1.jpg",
        link: "/work/telegram-bot",
        tags: ["AI", "Bot", "Assistant", "Automation"],
        aspect: "1:1",
        customContent: `<p>The Telegram Production Assistant started as an experiment to understand whether I could control my computer using simple commands sent via Telegram. After seeing people build small automated services and bots, I wanted to explore whether I could control my workstation while working remotely.</p>

<p>The first commands I integrated were <code>/status</code> and <code>/newproject</code>, which ran local .cmd files and returned structured feedback directly to Telegram.</p>

<p>Since then, the system has grown and continues to evolve. Below is an outline of the core functions currently implemented.</p>

<hr style="margin: 2rem 0; border: none; border-top: 1px solid rgba(255, 255, 255, 0.2);" />

<h2>Core Functions</h2>

<h3>/status</h3>
<p>Checks the status of the computer, including hardware utilisation and temperature. This allows me to remotely confirm that the system is running safely and diagnose issues without direct access.</p>

<h3>/morningweather</h3>
<p>A simple utility that retrieves the morning weather forecast for Bristol.</p>

<img src="/images/telegram-bot/TWA-SMW.png" alt="Telegram bot morning weather command" style="width: 100%; max-width: 600px; margin: 1.5rem 0; border-radius: 8px;" />

<h3>/newproject</h3>
<p>Creates a predefined folder structure with templated files, reducing setup time when starting new projects.</p>

<h3>/listprojects</h3>
<p>Displays active projects and their folders. Completed projects are rotated to a backup drive to avoid cluttering working directories and Dropbox.</p>

<img src="/images/telegram-bot/TWA-newproject.png" alt="Telegram bot new project command" style="width: 100%; max-width: 600px; margin: 1.5rem 0; border-radius: 8px;" />

<h3>/whosThere</h3>
<p>Runs a local network scan using nmap to report connected devices. This provides situational awareness of network activity and lan occupancy.</p>

<img src="/images/telegram-bot/TWA-WT.png" alt="Telegram bot network scan command" style="width: 100%; max-width: 600px; margin: 1.5rem 0; border-radius: 8px;" />

<h3>/owarun</h3>
<p>Integrates the Ordos Work Agent (OWA), an automated AI system designed to help find and bid for projects. OWA scrapes multiple commission sources, cross-references them with my portfolio, scores relevance, and assists with proposal drafting using configurable filters such as budget, timeframe, location, and skillset.</p>

<video src="/images/telegram-bot/ordosWorkAgent.webm" controls autoplay loop muted playsInline style="width: 100%; max-width: 800px; margin: 1.5rem 0; border-radius: 8px; display: block;" poster="/images/telegram-bot/telegrambot1.jpg">
    Your browser does not support the video tag.
</video>

<h3>/audiodownload · /summarize · /pdf</h3>
<p>These commands are part of a single local summarisation tool built to convert long-form audio or YouTube content into concise PDFs suitable for reading on a Kindle.</p>

<p>The workflow follows a simple pipeline:</p>
<p><strong>Input → audio download → transcription → markdown summary → PDF output.</strong></p>

<p>The system runs fully locally using Python, Ollama, Whisper, and Mistral, without relying on cloud-based services.</p>`
    },
    {
        id: 9,
        title: "Tat Plus Vision",
        category: "Theatre",
        year: "2021",
        description: "Live video and motion capture performance featuring facial tracking and avatar animation using Microsoft Kinect.",
        image: "/images/tat-plus-vision/tatface.webm",
        link: "/work/tat-plus-vision",
        tags: ["Game Development", "Motion Graphics", "Interactive Installation","Theatre"],
        aspect: "4:5",
        images: [
            "/images/tat-plus-vision/tat1.jpeg",
            "/images/tat-plus-vision/tat2.webm",
            "/images/tat-plus-vision/tat3.webm"
        ],
        customContent: `<p>In collaboration with artist <a href="https://instagram.com/tat_vision" target="_blank" rel="noopener noreferrer">Tat Vision</a>, I created a live video and motion capture performance featuring facial tracking and avatar animation. Using skeletal data from Microsoft Kinect, the project brought a 3D avatar to life on screen in real-time.</p>

<p>The original model was 3D scanned and then rigged in 3D before being sent to Unity for the motion capture rig. The performance system featured interactive capabilities including picking up objects, changing scenes dynamically, and switching camera angles for dramatic effect — all controlled in real-time during the live performance.</p>

<p><a href="https://www.glee.co.uk/performer/tat-vision-live/" target="_blank" rel="noopener noreferrer">https://www.glee.co.uk/performer/tat-vision-live/</a></p>`
    },
    {
        id: 12,
        title: "Imbue - Freddo",
        category: "3D Rendering",
        year: "2020",
        description: "Interactive brand experience for Freddo chocolate using motion graphics and touch interfaces.",
        image: "/images/imbue-freddo/Freddo Renders_0001_freddo_3_0127.jpg",
        link: "/work/imbue-freddo",
        tags: ["3D Modeling", "Fabrication", "Contemporary Art"],
        aspect: "1:1",
        images: [
            "/images/imbue-freddo/Freddo Renders_0000_freddo_0127.jpg",
            "/images/imbue-freddo/Freddo Renders_0001_freddo_3_0127.jpg",
            "/images/imbue-freddo/Freddo Renders_0002_freddo_2_0127.jpg"
        ],
        customContent: "An interactive brand experience created for Freddo chocolate that combines 3D modeling, motion graphics, and touch interfaces to create an engaging consumer experience. The project explores how digital technology can enhance brand interaction, using tactile interfaces and responsive visuals to create memorable moments of connection between consumers and the brand."
    },
    {
        id: 13,
        title: "Foka wolf - Gold Bar",
        category: "Fabrication",
        year: "2020",
        description: "Digital street art project exploring themes of value and materialism through augmented reality.",
        image: "/images/foka-wolf-gold-bar/Gold-bar_0001_Goldbar 1.jpg",
        link: "/work/foka-wolf-gold-bar",
        tags: ["Street Art", "3D Modeling", "Fabrication"],
        aspect: "5:4",
        images: [
            "/images/foka-wolf-gold-bar/Gold-bar_0000_Goldbar 2.jpg",
            "/images/foka-wolf-gold-bar/Gold-bar_0001_Goldbar 1.jpg"
        ],
        customContent: "A digital street art project that explores themes of value, materialism, and economic inequality through augmented reality. The project uses 3D modeling and fabrication techniques to create physical objects that serve as triggers for AR experiences, blending digital and physical art practices to create new forms of public engagement with social and economic issues."
    },
    {
        id: 21,
        title: "Angkor Wat - Unesco Heritage Site 3D Scanning",
        category: "3D Scanning",
        year: "2025",
        description: "3D scanning project documenting the UNESCO World Heritage Site of Angkor Wat, capturing detailed architectural elements and textures for digital preservation and study.",
        image: "/images/angkor-wat/Angkor wat scans__0002_Photo 23-09-2025, 13 52 02.jpg",
        link: "/work/angkor-wat",
        tags: ["3D Scanning", "Heritage Site", "3D Modeling"],
        aspect: "5:4",
        images: [
            "/images/angkor-wat/Angkor wat scans__0000_Photo 23-09-2025, 13 53 28.jpg",
            "/images/angkor-wat/Angkor wat scans__0001_Photo 23-09-2025, 13 52 46.jpg",
            "/images/angkor-wat/Angkor wat scans__0002_Photo 23-09-2025, 13 52 02.jpg",
            "/images/angkor-wat/Angkor wat scans__0003_Photo 23-09-2025, 13 51 30.jpg",
            "/images/angkor-wat/Angkor wat scans__0004_Photo 23-09-2025, 13 50 43.jpg",
            "/images/angkor-wat/Angkor wat scans__0005_Photo 23-09-2025, 13 55 01.jpg"
        ],
        customContent: "A comprehensive 3D scanning project documenting the UNESCO World Heritage Site of Angkor Wat in Cambodia. Using advanced photogrammetry and 3D scanning techniques, this project captures the intricate architectural details, textures, and spatial relationships of this ancient temple complex. The resulting digital models serve as a permanent record for preservation, research, and educational purposes, ensuring that future generations can study and experience this cultural treasure even as the physical structure faces environmental and time-based challenges."
    },
    {
        id: 22,
        title: "Christ in Bag",
        category: "3D Modeling",
        year: "2023",
        description: "3D modeling and installation project exploring themes of faith, materiality, and contemporary art through sculptural representation.",
        image: "/images/christ-in-bag/Christ in bag__0002_Christ in bag_New_Take.jpg",
        link: "/work/christ-in-bag",
        tags: ["3D", "Installation", "Collaboration"],
        aspect: "4:5",
        images: [
            "/images/christ-in-bag/Christ in bag__0000_Christ in bag_New_Take_2.jpg",
            "/images/christ-in-bag/Christ in bag__0001_Christ in bag_New_Take_1.jpg",
            "/images/christ-in-bag/Christ in bag__0002_Christ in bag_New_Take.jpg"
        ],
        customContent: "A collaborative 3D modeling and installation project that explores themes of faith, materiality, and contemporary art through sculptural representation. The project examines the intersection of religious iconography and everyday objects, creating thought-provoking installations that challenge traditional notions of sacred and profane. Using advanced 3D modeling techniques, the work creates new forms of spiritual expression that resonate with contemporary audiences."
    },
    {
        id: 26,
        title: "Unhappy Meal (Grimace)",
        category: "3D Modeling",
        year: "2023",
        description: "A collaborative sculptural installation subverting fast-food branding, reconstructing a counterfeit 'Happy Meal' to explore the fragility beneath corporate optimism.",
        image: "/images/grimace/grim2.jpg",
        link: "/work/grimace",
        tags: ["3D Modeling", "Fabrication", "Collaboration", "Installation", "Exhibition"],
        aspect: "5:4",
        modelPath: "/work/grimace/glbs/Grimice-artpeice.glb",
        images: [
            "/images/grimace/grim1.jpg",
            "/images/grimace/grim2.jpg"
        ],
        customContent: `<p><strong>Jacob Faulkner &amp; Sam Brentnall</strong><br />
Backhand Exhibition, March 2023</p>

<p>Unhappy Meal is a collaborative sculptural installation responding to the exhibition prompt "dreary." The work subverts the familiar language of fast-food branding by reconstructing a counterfeit "Happy Meal" from scratch.</p>

<p>The installation consists of a fully recreated meal: custom-designed packaging, printed wrappers and ephemera, a tray, and real food elements including fries and a burger. At its centre is the toy — a distorted reimagining of Grimace — which was 3D modelled, printed, hand-painted, and presented hanging, reframing the mascot as a symbol of exhaustion, despair, and the emotional fallout of hyper-commercialised culture.</p>

<p>By closely mimicking the visual language of a globally recognisable brand while stripping it of its promised joy, Unhappy Meal exposes the fragility beneath corporate optimism. The work uses dark humour and uncomfortable familiarity to question how consumer culture packages happiness, particularly for children, and what remains when those narratives collapse.</p>

<p>Presented as part of the Backhand Exhibition, the piece functions as both a sculptural object and a staged scene — inviting viewers to confront the uneasy intersection of branding, consumption, and mental health.</p>`
    },
    
    {
        id: 28,
        title: "Key To Our Hearts",
        category: "3D Modeling",
        year: "2024",
        description: "Project entry placeholder for Shambala Key.",
        image: "/images/shambala-key/shambalaKey (3).jpg",
        link: "/work/shambala-key",
        tags: ["3D Modeling", "Fabrication", "Collaboration"],
        aspect: "1:1",
        hidden: true,
        images: [
            "/images/shambala-key/shambalaKey (1).jpg",
            "/images/shambala-key/shambalaKey (2).jpg",
            "/images/shambala-key/shambalaKey (3).jpg",
            "/images/shambala-key/shambalaKey (4).jpg"
        ],
        customContent: "Commissioned to design and sculpt a bespoke commemorative key for Shambhala Festival, created as a symbolic gift for the organisation's team. Using Cinema 4D and digital sculpting, I developed an ornate, gothic-inspired design that incorporated the festival's logo. Each key was high-fidelity 3D printed in resin, moulded, and cast in bronze, with unique numbering from 1–20. This project required fine attention to detail, translating digital ornamentation into a tangible, highly crafted sculptural edition."
    },
    {
        id: 29,
        title: "Sickboy Landscape",
        category: "3D Modeling",
        year: "2022",
        description: "Perspective visualisation collaboration with Sickboy, recreating hand-drawn sketches as high-resolution 3D scenes for large-scale print production.",
        image: "/images/sickboy-landscape/sb1.webm",
        link: "/work/sickboy-landscape",
        tags: ["3D Modeling", "Rendering", "Collaboration"],
        aspect: "5:4",
        customContent: `<h2>Perspective Visualisation – Collaboration with Sickboy</h2>
<h3>(Large-Scale Print Preparation)</h3>

<p>During my time working at <a href="https://www.3dpk.co.uk" target="_blank" rel="noopener noreferrer">3DPK CiC</a> I recreated a series of hand-drawn sketches by British street artist <a href="https://www.instagram.com/sickboykks" target="_blank" rel="noopener noreferrer">Sickboy</a> as high-resolution 3D scenes in Cinema 4D. Produced multiple perspectives with accurate lighting and shadow detail, enabling the work to be scaled to very large formats while maintaining depth and fidelity. Delivered ultra-high-resolution renders as a visual foundation for print production, providing the artist with precise references for sketching and finalising the artwork.</p>

<img src="/images/sickboy-landscape/sb2.jpg" alt="Sickboy Landscape 3D visualisation" style="width: 100%; max-width: 900px; margin: 1.75rem 0; border-radius: 8px;" />

<img src="/images/sickboy-landscape/sb3.jpg" alt="Sickboy Landscape 3D visualisation" style="width: 100%; max-width: 900px; margin: 1.75rem 0; border-radius: 8px;" />

<video src="/images/sickboy-landscape/sb1.webm" autoplay loop muted playsInline style="width: 100%; max-width: 900px; margin: 1.75rem 0; border-radius: 8px; display: block;"></video>`
    },
    {
        id: 30,
        title: "Tom Davis - Sculpture Digitisation",
        category: "3D Scanning",
        year: "2021",
        description: "3D scanning and digitisation project for artist Tom Davis, capturing detailed sculptural works for digital preservation and documentation.",
        image: "/images/tom-davis/_0000_mask_Main2.jpg",
        link: "/work/tom-davis",
        tags: ["3D Scanning", "3D Modeling", "Collaboration"],
        aspect: "4:5",
        images: [
            "/images/tom-davis/_0000_mask_Main2.jpg",
            "/images/tom-davis/_0001_mask_New_Take_12.jpg",
            "/images/tom-davis/_0002_Humunculus_Main2.jpg",
            "/images/tom-davis/_0003_Humunculus_Main.jpg"
        ],
        customContent: "A comprehensive 3D scanning and digitisation project for artist Tom Davis, capturing detailed sculptural works for digital preservation and documentation. Using advanced photogrammetry and 3D scanning techniques, this project creates high-fidelity digital models of physical sculptures, ensuring their preservation and enabling new forms of digital interaction and study."
    },
    {
        id: 31,
        title: "Google Dorker Sequencer",
        category: "Creative Technology",
        year: "2025",
        description: "Interactive web tool for generating and sequencing Google search queries.",
        image: "/images/google-dorker-sequencer/Google_Dorker_sequencer.jpg",
        link: "/work/google-dorker-sequencer",
        externalLink: "/work/dorker/index.html",
        tags: ["OSINT", "Creative Coding", "Research Tool"],
        aspect: "5:4",
        customContent: "An interactive web tool for generating and sequencing Google search queries, designed for OSINT (Open Source Intelligence) research and creative exploration. The tool combines creative coding with practical research applications, enabling users to discover hidden information and explore the vast landscape of publicly available data through innovative search techniques."
    },
    {
        id: 32,
        title: "Bangkok Record Store",
        category: "Interactive Installation",
        year: "2025",
        description: "A 3D model of a record store in Bangkok, rendered in real-time using Three.js. Explore the interior and exterior of this detailed architectural model.",
        image: "/images/bangkok-recordstore/bkrs.jpg",
        link: "/work/bangkok-recordstore",
        externalLink: "/work/bangkok-recordstore/",
        tags: ["Interactive", "3D Modeling", "WebGL", "Architecture"],
        aspect: "16:9",
        customContent: "An interactive 3D visualisation of a record store in Bangkok, created using Three.js and real-time rendering. This project allows users to explore a detailed architectural model of the store, navigating both interior and exterior spaces. The interactive viewer features customisable lighting controls, smooth camera navigation, and high-fidelity 3D models that capture the unique character and atmosphere of the Bangkok record store."
    },
    {
        id: 33,
        title: "Art Scans",
        category: "3D Scanning",
        year: "2025",
        description: "An interactive 3D viewer documenting exhibitions, art shows, and pop-ups. Each scan captures the spatial experience of these temporary art spaces, preserving them in digital form.",
        image: "/images/art-scans/art-scans.jpg",
        link: "/work/art-scans",
        externalLink: "/work/art-scans/",
        tags: ["3D Scanning", "Interactive", "Documentation", "WebGL", "Exhibition"],
        aspect: "16:9",
        customContent: "An interactive 3D viewer for documenting exhibitions, art shows, and pop-ups. Each scan captures the spatial experience of these temporary art spaces, preserving them in digital form. The viewer uses Three.js to render 3D models in real-time, allowing users to explore exhibition spaces that would otherwise be lost after the shows end. The project includes metadata tracking for each scan, including exhibition names, dates, participating artists, and locations, creating a digital archive of contemporary art spaces."
    }
];

export const getProjectsByCategory = (category) => {
    return projects.filter(project => project.category === category);
};

export const getProjectsByYear = (year) => {
    return projects.filter(project => project.year === year);
};

export const getProjectsByTag = (tag) => {
    return projects.filter(project => project.tags.includes(tag));
};

// Helper function to convert project title to slug
export const getProjectSlug = (title) => {
    return title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
};

// Helper function to find project by slug
export const getProjectBySlug = (slug) => {
    return projects.find(project => getProjectSlug(project.title) === slug);
};

// Helper function to check if project is external
export const isExternalProject = (project) => {
    return project.externalLink !== null && project.externalLink !== undefined;
}; 